import styled from '@emotion/styled';
import React from 'react';
import { Theme } from '../../../../../../../theme';
import {
  getCancelInfoStr,
  getGoalTypeStr,
  getRateInfoStr,
  getStatusStr,
} from '../../translations';
import {
  GoalCategory,
  GoalEmployee,
  GoalRateInfo,
  GoalRelatedGoal,
  GoalStatus,
  GoalTag,
  GoalTeam,
  GoalType,
  GoalVisibility,
  WeightInfo,
} from '../../types';
import { GoalLabel } from './GoalLabel';
import { GoalTagLabels } from './GoalTagLabels';
import { GoalVisibilityLabel } from './GoalVisibilityLabel';
import { GoalWeightLabel } from './GoalWeightLabel';
import { useGoalsContext } from '../../GoalsContext/GoalsContext';
import { useGoalViewBy } from '../../useGoalViewBy';
import { GoalRelatedLabel } from './GoalRelatedLabel';
import { GoalResultScaleLabel } from './GoalResultScaleLabel';
import {
  ObjectiveMode,
  ObjectiveWeightMode,
} from '../../../../../__generated__/graphql';
import { formatPredefinedResultOptionLabel } from '../../logic';

type Props = {
  goalType: GoalType;
  rateInfo: GoalRateInfo;
  status: GoalStatus;
  periodName: string | undefined;
  visiblity: GoalVisibility;
  sharedWith?: GoalEmployee[]; // When visibility == protected
  goalId: string;
  tags: GoalTag[];
  cancelReason?: string;
  statusChangedBy?: GoalEmployee;
  category: GoalCategory | null;
  achievementScaleId?: string | null;
  achievementScaleName?: string | null;
  achievementScore?: number | null;
  finalWeight?: number | null;
  weightedAchievementScore?: number | null;
  displayResult?: string | null;
  parent: GoalRelatedGoal | null;
  children: GoalRelatedGoal[];
  team?: GoalTeam;
  weightInfo?: WeightInfo;
  employee?: GoalEmployee;
  onClick?: () => void;
  canEditResult?: boolean;
};

// todo: In next PR. "Add Canceled by or not implement!?
export const GoalCardLabels: React.FC<Props> = ({
  goalType,
  status,
  periodName,
  goalId,
  visiblity,
  sharedWith,
  rateInfo,
  tags,
  cancelReason,
  statusChangedBy,
  category,
  achievementScaleId,
  achievementScaleName,
  achievementScore,
  finalWeight,
  weightedAchievementScore,
  displayResult,
  parent,
  children,
  team,
  weightInfo,
  employee,
  onClick,
  canEditResult,
}) => {
  const {
    developmentFrameworkEnabled,
    isManagerView,
    objectiveMode,
    objectiveWeightMode,
    employeeAssignedCategories,
  } = useGoalsContext();
  // it uses local storage value, it doesn't create new state
  const [viewBy] = useGoalViewBy();

  const formattedDisplayResult = (() => {
    if (!isManagerView || displayResult == null || !category)
      return displayResult;
    const assignment = employeeAssignedCategories.find(
      (a) => a.category.Id === category.id,
    );
    const predefinedOption = assignment?.category.predefinedResultOptions.find(
      (opt) => opt.Name === displayResult,
    );
    if (!predefinedOption) return displayResult;
    return formatPredefinedResultOptionLabel(
      predefinedOption.Name,
      predefinedOption.flair__Result_Value__c,
    );
  })();
  const allLabels = status === 'BEHIND' || status === 'ON_TRACK';
  const isRatedVisible =
    rateInfo.rate && (rateInfo.rate !== 'OPEN' || status === 'DONE');
  //Canceled by John Doe (HR) due to “change on priorities”
  const isCancelVisible =
    status === 'CANCELED' && cancelReason && statusChangedBy;

  const isCategoryVisible =
    category && developmentFrameworkEnabled && viewBy !== 'category';

  const isStatusVisible =
    viewBy !== 'status' && objectiveMode !== ObjectiveMode.Mbo;

  return (
    <Container>
      {isManagerView && employee && (
        <GoalLabel
          icon="person-outline"
          label={employee.name}
          className="me-3"
          noWrap={true}
        />
      )}
      {team && (
        <GoalLabel
          icon="people-outline"
          label={team.name}
          className="me-3"
          noWrap={true}
        />
      )}
      <GoalLabel
        icon="bullseye-outline"
        label={getGoalTypeStr(goalType)}
        className="me-3"
        noWrap={true}
      />
      {isStatusVisible && (
        <GoalLabel
          icon="phosphor-pulse"
          label={getStatusStr(status)}
          className="me-3"
        />
      )}
      <GoalRelatedLabel
        goalId={{ id: goalId, type: goalType }}
        parent={parent}
        children={children}
      />
      {isCategoryVisible && category && (
        <GoalLabel
          icon="phosphor-stack"
          label={category.name}
          className="me-3"
        />
      )}
      {weightInfo && objectiveWeightMode !== ObjectiveWeightMode.None && (
        <GoalWeightLabel
          weightInfo={weightInfo}
          goalId={goalId}
          onClick={onClick}
        />
      )}
      {isRatedVisible && (
        <GoalLabel
          icon="information-circle-filled"
          label={getRateInfoStr(rateInfo)}
          className="me-3"
        />
      )}
      {isCancelVisible && (
        <GoalLabel
          icon="information-circle-filled"
          label={getCancelInfoStr(cancelReason, statusChangedBy)}
          className="me-3"
        />
      )}

      {allLabels && (
        <>
          {!developmentFrameworkEnabled && periodName && (
            <GoalLabel
              icon="tilde-outline"
              label={periodName}
              className="me-3"
            />
          )}

          <GoalVisibilityLabel
            goalId={goalId}
            visiblity={visiblity}
            sharedWith={sharedWith}
          />
          <GoalTagLabels goalId={goalId} tags={tags} />
        </>
      )}
      {developmentFrameworkEnabled && objectiveMode === ObjectiveMode.Mbo && (
        <GoalResultScaleLabel
          goalId={goalId}
          achievementScaleId={achievementScaleId}
          achievementScaleName={achievementScaleName}
          achievementScore={achievementScore}
          finalWeight={finalWeight}
          weightedAchievementScore={weightedAchievementScore}
          displayResult={formattedDisplayResult}
          onClick={onClick}
          canEditResult={canEditResult}
        />
      )}
    </Container>
  );
};

const Container = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  color: Theme.color.dark,
  fontSize: Theme.font.size.sm,
});
